import { log } from '../../util/log';
import { BookingContext } from '../../util/booking-context';
import { callWp } from '../../util/call-wp';
import { hideLoadingOverlay, showLoadingOverlay } from '../../util/loading-overlay';
import { sleep } from '../../util/sleep';
import { loadConfirmAppointmentPage } from './confirm-appointment';
import { loadChooseServicesPage } from './choose-services';
import { loadSelectLocationPage } from './select-location';
import { bindSummary, updateSummary } from '../../util/summary';
import { track } from '../../util/tracking';

export async function loadFindTimeslotPage(context: BookingContext) {
	log('context', context.data);
	let shouldStop = false;
	window.scroll(0, 0);
	log('Loading find timeslot page');
	disableBackButton();

	// IF there is no date set, then set the date to today
	// if (!context.data.date) {
	// 	const today = new Date();
	// 	const year = today.getFullYear();
	// 	const month = today.getMonth() + 1;
	// 	const day = today.getDate();
	// 	context.data.date = `${year}-${month}-${day}`;
	// }

	showLoadingOverlay(context);
	context.root.innerHTML = '';
	const ssrStartTime = performance.now();

	const depositRequired = context.data.categoryName === 'Cosmetic Injectables' && !context.data.reschedule;
	const ssrResponse = await callWp('GET', 'view_find_timeslot', { depositRequired, ...context.data });
	const ssrEndTime = performance.now();
	log('SSR took', ssrEndTime - ssrStartTime, 'ms');

	const { html, error, consultData } = ssrResponse;

	if (error) {
		log('Error loading find timeslot page:', error);
		context.root.innerHTML = error;
		return;
	}

	if (consultData) {
		context.data.consultData = consultData;
	}

	context.root.innerHTML = html;

	bindSummary(context);

	hideLoadingOverlay(context);

	if (!context.data.reschedule && !context.data.book) {
		context.root.querySelector('[data-step="0"]')?.addEventListener('click', () => {
			shouldStop = true;
			loadSelectLocationPage(context);
		});

		context.root.querySelector('[data-step="1"]')?.addEventListener('click', () => {
			shouldStop = true;
			loadChooseServicesPage(context);
		});
	}

	context.root.querySelectorAll<HTMLButtonElement>('button[data-reload]').forEach((b) =>
		b.addEventListener('click', () => {
			shouldStop = true;
			loadFindTimeslotPage(context);
		})
	);

	const nextButton = context.root.querySelector<HTMLButtonElement>('[data-next]');
	const backButton = context.root.querySelector<HTMLButtonElement>('[data-back]');
	if (!nextButton) {
		log('No next button found');
		return;
	}
	// if (!backButton) {
	// 	log('No next button found');
	// 	return;
	// }

	// Calendar
	const calendar = context.root.querySelector<HTMLDivElement>('[data-calendar]');
	if (!calendar) {
		log('No calendar found');
		return;
	}

	const chooseTimeButton = context.root.querySelector<HTMLButtonElement>('[data-choose-time]');
	const earliestTimeButton = context.root.querySelector<HTMLButtonElement>('[data-earliest-time]');
	const timeslotsListSection = context.root.querySelector<HTMLDivElement>('[data-timeslots-list]');

	if (!chooseTimeButton || !earliestTimeButton || !timeslotsListSection) {
		log('No choose time or earliest time button or section found');
		return;
	}

	// On the calendar next and previous buttons, add an event listener to hide this month element and show the next or previous month element
	const calendarNextButtons = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-next]');
	calendarNextButtons.forEach((calendarNext) =>
		calendarNext.addEventListener('click', () => {
			log('Calendar next clicked');
			const currentMonth = calendar.querySelector<HTMLElement>('[data-calendar-month]:not(.hidden)');
			if (!currentMonth) {
				return;
			}
			const nextMonth = currentMonth.nextElementSibling as HTMLElement;
			if (!nextMonth) {
				return;
			}
			currentMonth.classList.add('hidden');
			nextMonth.classList.remove('hidden');
		})
	);
	const calendarPreviousButtons = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-prev]');
	calendarPreviousButtons.forEach((calendarPrevious) =>
		calendarPrevious.addEventListener('click', () => {
			log('Calendar previous clicked');
			const currentMonth = calendar.querySelector<HTMLElement>('[data-calendar-month]:not(.hidden)');
			if (!currentMonth) {
				return;
			}
			const previousMonth = currentMonth.previousElementSibling as HTMLElement;
			if (!previousMonth) {
				return;
			}

			currentMonth.classList.add('hidden');
			previousMonth.classList.remove('hidden');
		})
	);

	// For each of the calendar days, add an event listener to load the timeslots for that day
	const calendarDays = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-day]:not(disabled)');
	calendarDays.forEach((calendarDay) => {
		calendarDay.addEventListener('click', async () => {
			log('Calendar day clicked');
			const date = calendarDay.dataset.date;
			shouldStop = true;

			if (!date) {
				return;
			}

			context.data.date = date;

			calendarDays.forEach((btn) => {
				btn.classList.toggle('timeslots__calendar-day--selected', false);
				btn.disabled = false;
			});

			calendarDay.classList.toggle('timeslots__calendar-day--selected', true);
			calendarDay.disabled = true;

			updateSummary(context, rerenderRows);

			timeslotsListSection.classList.toggle('hidden', false);

			// disable input on the calendar while loading
			calendarDays.forEach((btn) => (btn.disabled = true));

			timeslotsListSection.innerHTML = loadingSpinner();
			let fragmentHtml = await loadTimeslotsForDate(context);

			calendarDays.forEach((btn) => (btn.disabled = false));

			if (fragmentHtml === undefined || fragmentHtml === null) {
				track({
					event: 'bookingError',
					message: 'Error loading timeslots',
				});
				// fragmentHtml = 'An unknown error has occured. Please try again later.';
				return;
			}

			if (fragmentHtml.length === 0) {
				fragmentHtml = '<div class="spin__container">No available bookings. Please select another date or call your clinic to book.</div>';
			}

			timeslotsListSection.innerHTML = fragmentHtml;

			rebindTimeslotButtons();
		});
	});

	// For each of the timeslots, add an event listener to load the confirm appointment page
	const rebindTimeslotButtons = () => {
		const slots = context.root.querySelectorAll<HTMLButtonElement>('button[data-slot]');
		slots.forEach((slot) => {
			if (slot.dataset.serviceBookingId === context.data.serviceBookingId) {
				slot.classList.toggle('timeslots__list-item--active', true);
			}

			slot.addEventListener('click', async () => {
				log('Timeslot clicked');
				const time = slot.dataset.time;
				const serviceBookingId = slot.dataset.serviceBookingId;
				if (!time || !serviceBookingId) {
					return;
				}

				slots.forEach((btn) => {
					btn.classList.toggle('timeslots__list-item--active', false);
				});

				slot.classList.toggle('timeslots__list-item--active', true);

				context.data.slotTime = time;

				const displayDate = slot.dataset.displayDate;
				const displayTime = slot.dataset.displayTime;

				context.data.displayDate = displayDate ?? null;
				context.data.displayTime = displayTime ?? null;

				context.data.serviceBookingId = serviceBookingId;

				nextButton.disabled = false;

				updateSummary(context, rerenderRows);
			});
		});
	};

	backButton?.addEventListener('click', async () => {
		shouldStop = true;
		// if resschedule, go back to my account, otherwise go back to choose services
		if (context.data.reschedule) {
			showLoadingOverlay(context);
			window.location.href = silkNexusWp.myAccountUrl + '/appointments';
			return;
		}

		return loadChooseServicesPage(context);
	});

	nextButton.addEventListener('click', async () => {
		shouldStop = true;

		log('Next button clicked');

		if (!context.data.slotTime || !context.data.serviceBookingId) {
			return;
		}

		showLoadingOverlay(context);

		const response = await reserveBooking(context);

		if (response?.error || !response?.success) {
			track({
				event: 'bookingError',
				message: 'Error reserving appointment',
			});
			log('Error reserving appointment:', response.error || 'An unknown error occurred');

			if (!document.getElementById('unexpectedErrorModal')) {
				const modalHTML = `
                    <div class="modal fade" id="unexpectedErrorModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                            <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                            <span>An unexpected error occurred. Please try again later.</span>
                            </div>
                            <div class="d-flex flex-row gap-2">
                            <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">CONTINUE</button>
                            </div>
                        </div>
                        </div>
                    </div>
                    `;
				document.body.insertAdjacentHTML('beforeend', modalHTML);
			}

			const modalElement = document.getElementById('unexpectedErrorModal')!;

			const modal = new window.bootstrap.Modal(modalElement, {
				backdrop: 'static',
				keyboard: false,
			});

			// on next, select clinic
			modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
				modal.hide();
				window.location.href = `${silkNexusWp.myAccountUrl}/appointments`;
			});

			modal.show();
			return;
		}

		await loadConfirmAppointmentPage(context);
	});

	updateSummary(context, rerenderRows);

	function rerenderRows() {
		updateSummary(context, rerenderRows);

		if (context.data.serviceIds.length === 0) {
			loadChooseServicesPage(context);
		}

		// Reload this page if the treatments change
		loadFindTimeslotPage(context);
	}

	chooseTimeButton.addEventListener('click', () => {
		shouldStop = true;
		// show the calendar
		// hide the earliest time slots

		if (context.data.date) {
			timeslotsListSection.classList.toggle('hidden', false);
		}

		chooseTimeButton.classList.toggle('timeslots__button--active', true);

		earliestTimeButton.classList.toggle('timeslots__button--active', false);
	});

	const loadEarliest = async () => {
		if (shouldStop) {
			shouldStop = false;
		}
		// Hide the calendar
		// show the earliest time slots

		timeslotsListSection.classList.toggle('hidden', false);

		chooseTimeButton.classList.toggle('timeslots__button--active', false);
		earliestTimeButton.classList.toggle('timeslots__button--active', true);

		// Get the first 7 calendar buttons that are not disabled
		const calendarButtons = Array.from(
			calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-day]:not(.timeslots__calendar-day--disabled)').values()
		).slice(0, 21);

		// For each of the calendar buttons, get the timeslots for that date
		// and display them in the earliest time slots section

		timeslotsListSection.innerHTML = loadingSpinner();

		let found = false;
		for (const calendarButton of calendarButtons) {
			const date = calendarButton.dataset.date;

			calendarDays.forEach((btn) => {
				btn.classList.toggle('timeslots__calendar-day--selected', false);
				btn.disabled = false;
			});

			// If the date is on the next calendar month, then go to the next month
			// and select the date
			if (calendarButton.closest('[data-calendar-month]')?.classList.contains('hidden')) {
				calendar.querySelectorAll('[data-calendar-month]').forEach((month) => {
					month.classList.toggle('hidden', true);
				});
				calendarButton.closest('[data-calendar-month]')?.classList.toggle('hidden', false);
			}

			calendarButton.classList.toggle('timeslots__calendar-day--selected', true);
			calendarButton.disabled = true;

			const timeslots = await loadTimeslotsForDate({ ...context, data: { ...context.data, date } });

			if (shouldStop) {
				shouldStop = false;
				return;
			}

			timeslotsListSection.innerHTML += timeslots;

			rebindTimeslotButtons();

			if (timeslots.length > 0) {
				found = true;
				break;
			}
		}

		if (!found) {
			timeslotsListSection.innerHTML =
				'<div class="spin__container">No available bookings found within the next few weeks. Please select another date or call your clinic to book.</div>';
		}

		timeslotsListSection.querySelector('[data-loading-spinner]')?.remove();
	};

	earliestTimeButton.addEventListener('click', async () => {
		loadEarliest();
	});

	//
	// Nurse / Therapist selection
	//
	const inputs = context.root.querySelectorAll<HTMLInputElement>('[data-therapist-select]');
	inputs.forEach((input) => {
		input.addEventListener('click', async () => {
			let therapistId: string | null = input.value;
			if (therapistId.length === 0) therapistId = null;

			const therapistWasNull = context.data.therapistId === null;
			context.data.therapistId = therapistId;

			if (!context.data.date && therapistWasNull) {
				loadEarliest();
				return;
			}

			shouldStop = true;

			calendarDays.forEach((btn) => btn.classList.toggle('disabled', true));
			timeslotsListSection.innerHTML = loadingSpinner();
			let fragmentHtml = await loadTimeslotsForDate(context);
			calendarDays.forEach((btn) => btn.classList.toggle('disabled', false));

			// if (fragmentHtml === undefined || fragmentHtml === null) {
			// 	track({
			// 		event: 'bookingError',
			// 		message: 'Error loading timeslots',
			// 	});
			// 	// fragmentHtml = 'An unknown error has occured. Please try again later.';
			// 	return;
			// }

			if (fragmentHtml.length === 0) {
				fragmentHtml = '<div class="spin__container">No available bookings. Please select another date or call your clinic to book.</div>';
			}

			timeslotsListSection.innerHTML = fragmentHtml;

			rebindTimeslotButtons();
		});
	});

	track({
		event: 'pageview',
		page_location: '/booking-online/schedule-visit/',
		page_title: 'Booking online - 3 - Schedule Visit',
	});
}

function reserveBooking(context: BookingContext) {
	return callWp('POST', 'reserve_booking', context.data);
}

function disableBackButton() {
	// history.pushState(null, document.title, location.href);
	// window.addEventListener('popstate', function (event) {
	// 	if (!this.confirm('You are about to leave the booking form. Are you want to cancel?')) {
	// 		history.pushState(null, document.title, location.href);
	// 	}
	// });
	// window.addEventListener('beforeunload', function (event) {
	// 	event.returnValue = 'You are about to leave the booking form. Are you want to cancel?';
	// });
}

async function loadTimeslotsForDate(context) {
	if (!context.data.date) {
		return;
	}
	const ssrStartTime = performance.now();
	const ssrResponse = await callWp('GET', 'view_find_timeslot_fragment', context.data);
	const ssrEndTime = performance.now();
	log('SSR took', ssrEndTime - ssrStartTime, 'ms');
	const { html, error } = ssrResponse;

	if (error) {
		track({
			event: 'bookingError',
			message: 'Error loading timeslots',
		});
		return `<div class="alert alert-danger" role="alert">${error}</div>`;
	}

	const bookingDay = context.data.date ? new Date(context.data.date).toLocaleDateString('en-AU', { weekday: 'long' }) : null;

	track({
		event: 'chooseDate',
		bookingDay,
		timeslotsAvailable: html.length > 0,
	});

	return html || '';
}

function loadingSpinner() {
	return `<div class="spin__container" data-loading-spinner><div class="spin__spinner"></div></div>`;
}
